import styled, { css } from 'styled-components';
import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { buildSectionLinkUrl } from 'homepage/utils/buildSectionLinkUrl';
import {
  DESKTOP_PADDING,
  DESKTOP_GRID_IMAGE_SIZE,
  MOBILE_GRID_IMAGE_SIZE,
  MOBILE_PADDING,
} from 'homepage/styles/constants';
import { FeatureFlags } from 'featureFlags/constants';
import { fetchProductsForHomepageModule } from 'homepage/services/homepageApi';
import { getNameAlignmentVerticalOrderedGrid } from 'search/utils/getNameAlignment';
import { handleSectionClick } from 'homepage/utils/handleSectionClick';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { HomepageModuleHeader } from 'homepage/components/sections/HomepageModuleHeader';
import { SearchResultImageGrid } from 'search/components/search/SearchResultImageGrid';
import { LoaderImageResultsGrid } from 'homepage/components/productGridSection/LoaderImageResultsGrid';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import {
  useFeatureFlag,
  useIsElementInViewport,
  useWindowSize,
  useIsMounted,
  useAbortController,
} from 'shared/hooks';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';

import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { IConstructorUserParams } from 'search/types/ConstructorUserParams';
import { IModuleTrackingData } from 'homepage/types/ModuleTrackingData';
import { ISearchConfig } from 'homepage/types/SectionSearchConfig';
import { setCurrentFeedData } from 'store/actions/homepageActions';

export interface IProductGridSection {
  constructorResponse: IConstructorBrowseResponse;
  constructorUserParams?: IConstructorUserParams;
  sectionIndex: number;
  moduleTrackingData: IModuleTrackingData;
  searchConfig: ISearchConfig;
  title: string;
  isFullScreenGrid?: boolean;
  isInViewPort?: boolean;
  customAnimation?: string;
}

export const ProductGridSection = ({
  constructorResponse,
  constructorUserParams,
  sectionIndex,
  moduleTrackingData,
  searchConfig,
  title,
  isInViewPort = false,
  isFullScreenGrid = false,
  customAnimation,
}: IProductGridSection) => {
  const isDiscoverTabHomepageBackground = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_ADD_DISCOVER_TO_HOMEPAGE_BACKGROUND,
  );

  const { isDiscoverTabOpen, isLoading } = useStoreSelector((state) => state.homepage);
  const { isMobile, windowSize } = useWindowSize();

  const storeDispatch = useStoreDispatch();
  const moduleRef = useRef();
  const initialInViewport = useIsElementInViewport(moduleRef);

  const [data, setData] = useState(constructorResponse);
  const [isModuleInViewport, setIsModuleInViewport] = useState(initialInViewport);
  const [numOfShoeImagesToShow, setNumOfShoeImagesToShow] = useState(0);
  const [numOfColumns, setNumOfColumns] = useState(5);
  const [numOfRows, setNumOfRows] = useState(5);
  const [hasTabBeenOpened, setHasTabBeenOpened] = useState(false);
  const { signal } = useAbortController()();
  const isMounted = useIsMounted();

  const { pathname } = useRouter();

  const getProducts = async (abortSignal) => {
    const response = await fetchProductsForHomepageModule(
      searchConfig,
      constructorUserParams,
      0,
      abortSignal,
    );
    setData(response);
  };

  useEffect(() => {
    const getNumberOfImagesToRender = () => {
      const num = isMobile
        ? Math.floor((windowSize.width - 32) / MOBILE_GRID_IMAGE_SIZE)
        : Math.floor((windowSize.width - DESKTOP_GRID_IMAGE_SIZE) / DESKTOP_GRID_IMAGE_SIZE);
      const gridCapCost = isMobile ? num * 8 : num * 20;
      const numOfColumnsToShow = isMobile
        ? Math.floor((windowSize.width - 32 - gridCapCost) / MOBILE_GRID_IMAGE_SIZE)
        : Math.floor(
            (windowSize.width - DESKTOP_GRID_IMAGE_SIZE - gridCapCost) / DESKTOP_GRID_IMAGE_SIZE,
          );
      setNumOfColumns(numOfColumnsToShow);
      const numOfRowsToShow = windowSize.width < 1280 && windowSize.height < 800 - 32 ? 4 : 5;
      setNumOfRows(numOfRowsToShow);
      setNumOfShoeImagesToShow(
        isMobile
          ? data?.response?.results?.length
          : numOfColumnsToShow * (isFullScreenGrid ? numOfRowsToShow : 4),
      );
    };

    getNumberOfImagesToRender();
  }, [windowSize]);

  useEffect(() => {
    if (!hasTabBeenOpened && isDiscoverTabOpen) {
      setHasTabBeenOpened(true);
    }
  }, [isDiscoverTabOpen]);

  useEffect(() => {
    if (isMounted()) {
      if (!isModuleInViewport) {
        setIsModuleInViewport(initialInViewport);
      } else if (typeof constructorResponse !== 'undefined' && !data) {
        setData(constructorResponse);
      } else if (isModuleInViewport && !isLoading && !data && !constructorResponse) {
        void getProducts(signal);
      }
    }
  }, [initialInViewport, isModuleInViewport, isLoading, data, constructorResponse]);

  useEffect(() => {
    if (data) {
      storeDispatch(setCurrentFeedData({ index: sectionIndex, constructorResponse: data }));
    }
  }, [data]);

  const linkUrl = buildSectionLinkUrl(searchConfig, data?.response?.groups) || '/';

  const handleSectionItemClick = (itemIndex, ptSlug) => {
    if (pathname.includes('gift-cards')) {
      sendTrackingEvent('GC_LANDING_PAGE_PDP_TAP', {
        product_template_slug: ptSlug,
      });
    } else {
      sendTrackingEvent('CMS_MODULE_ITEM_TAP', {
        ...moduleTrackingData,
        item_position: itemIndex,
        product_template_slug: ptSlug,
      });
    }
  };

  return data?.response?.results ? (
    <ProductGridSection.Container
      key={`prod-grid-${sectionIndex}`}
      ref={moduleRef}
      $isFullScreenGrid={isFullScreenGrid}
    >
      <HomepageModuleHeader
        count={data?.response?.total_num_results}
        onClick={() => handleSectionClick(moduleTrackingData)}
        linkUrl={linkUrl}
        title={title}
        isInViewPort={isInViewPort}
        customAnimation={customAnimation}
        isFullScreenGrid={isFullScreenGrid}
      />
      <ProductGridSection.ProductWall
        $isFullScreenGrid={isFullScreenGrid}
        $isInViewPort={isInViewPort}
        $customAnimation={customAnimation}
        $numOfRows={numOfRows}
      >
        {((!hasTabBeenOpened && !isDiscoverTabHomepageBackground) ||
          (!isModuleInViewport && !data)) &&
          data?.response?.results?.map((result, index) => (
            <ProductGridSection.GridImageContainer
              key={result?.data?.id}
              onClick={() => {
                handleSectionItemClick(index, result?.data?.slug);
              }}
            >
              <LoaderImageResultsGrid
                align={getNameAlignmentVerticalOrderedGrid(index, isMobile, numOfColumns)}
                product={result}
              />
            </ProductGridSection.GridImageContainer>
          ))}
        {(hasTabBeenOpened || isDiscoverTabHomepageBackground) &&
          (isModuleInViewport || data) &&
          data?.response?.results?.slice(0, numOfShoeImagesToShow)?.map((result, index) => (
            <ProductGridSection.GridImageContainer
              key={result?.data?.id}
              onClick={() => {
                handleSectionItemClick(index, result?.data?.slug);
              }}
            >
              <ProductGridSection.GridImageInnerContainer>
                <SearchResultImageGrid
                  align={getNameAlignmentVerticalOrderedGrid(index, isMobile, numOfColumns)}
                  imageSize={isFullScreenGrid && !isMobile ? '90px' : '82px'}
                  product={result}
                />
              </ProductGridSection.GridImageInnerContainer>
            </ProductGridSection.GridImageContainer>
          ))}
      </ProductGridSection.ProductWall>
    </ProductGridSection.Container>
  ) : (
    <ProductGridSection.Container
      key={`prod-grid-${sectionIndex}`}
      ref={moduleRef}
      $isFullScreenGrid={isFullScreenGrid}
    />
  );
};

ProductGridSection.Container = styled.div<{ $isFullScreenGrid: boolean }>`
  margin:  32px 0px;
  width: 100%;
  max-width: 100%;
  min-height: 436px;
  padding: 0px ${MOBILE_PADDING};
  padding-right: 0px;
  ${hideScrollbars};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: ${({ $isFullScreenGrid }) => ($isFullScreenGrid ? ` 0px 0px 20px 0px;` : `60px 0px;`)}
    padding: 0px ${DESKTOP_PADDING};
    min-height: 483.5px;
  }
`;

ProductGridSection.ProductWall = styled.div<{
  $isFullScreenGrid: boolean;
  $isInViewPort: boolean;
  $customAnimation?: string;
  $numOfRows?: number;
}>`
  ${({ $isFullScreenGrid, $isInViewPort, $customAnimation, $numOfRows }) =>
    $isFullScreenGrid
      ? css`
          display: grid;
          grid-template-rows: repeat(5, ${MOBILE_GRID_IMAGE_SIZE}px);
          grid-auto-flow: column;
          grid-gap: 8px;
          max-height: 490px;
          overflow: visible;
          max-width: calc(100vw - ${DESKTOP_PADDING});
          ${$customAnimation}
          @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
            margin-top: ${!$isInViewPort && '30px'};
            max-width: 100vw;
            grid-gap: 16px;
          }
          ${hideScrollbars};
          @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
            grid-template-rows: repeat(${$numOfRows}, ${DESKTOP_GRID_IMAGE_SIZE}px);
            grid-gap: 30px;
            min-height: ${$numOfRows * 110}px;
          }
        `
      : css`
          display: grid;
          grid-template-rows: repeat(5, ${MOBILE_GRID_IMAGE_SIZE}px);
          grid-auto-flow: column;
          grid-gap: 8px;
          max-height: 432px;
          overflow: hidden;
          max-width: calc(100vw - ${DESKTOP_PADDING});
          @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
            overflow-x: scroll;
            max-width: 100vw;
            grid-gap: 4px;
          }
          ${hideScrollbars};
          @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
            grid-template-rows: repeat(4, ${DESKTOP_GRID_IMAGE_SIZE}px);
            grid-gap: 20px;
            min-height: 440px;
          }
        `}
`;

ProductGridSection.GridImageContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  max-height: ${DESKTOP_GRID_IMAGE_SIZE}px;
  min-height: ${DESKTOP_GRID_IMAGE_SIZE}px;
  height: ${DESKTOP_GRID_IMAGE_SIZE}px;
  width: ${DESKTOP_GRID_IMAGE_SIZE}px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
    max-height: 82px;
    min-height: 82px;
    height: 82px;
    width: 82px;
  }
`;

ProductGridSection.GridImageInnerContainer = styled.div`
  max-height: ${DESKTOP_GRID_IMAGE_SIZE}px;
  min-height: ${DESKTOP_GRID_IMAGE_SIZE}px;
  height: ${DESKTOP_GRID_IMAGE_SIZE}px;
  width: ${DESKTOP_GRID_IMAGE_SIZE}px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
    max-height: 82px;
    min-height: 82px;
    height: 82px;
    width: 82px;
  }
`;
